import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionApi } from '../services/api';
import '../styles/SessionCreation.css';

const TherapistSessionCreationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    topic: 'meditation',
    price: 0,
    mode: 'online',
    location: '',
    scheduledAt: '',
    endsAt: '',
    maxParticipants: 20,
    zoomMeetingLink: '',
    tags: [],
  });

  const topics = [
    'anxiety',
    'depression',
    'stress-management',
    'mindfulness',
    'relaxation',
    'breathing-techniques',
    'pain-management',
    'mobility',
    'flexibility',
    'strength-training',
    'posture',
    'injury-recovery',
    'meditation',
    'wellness',
    'nutrition',
    'lifestyle',
    'other',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate
      if (!formData.title.trim()) throw new Error('Title is required');
      if (!formData.scheduledAt) throw new Error('Start time is required');
      if (!formData.endsAt) throw new Error('End time is required');

      const startTime = new Date(formData.scheduledAt);
      const endTime = new Date(formData.endsAt);

      if (startTime >= endTime) throw new Error('End time must be after start time');
      if (startTime < new Date()) throw new Error('Cannot schedule in the past');

      const response = await sessionApi.createSession(formData);

      if (response.success) {
        navigate('/therapist/sessions');
        // Show success toast/notification
      } else {
        throw new Error(response.message || 'Failed to create session');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="session-creation-container">
      <div className="session-creation-wrapper">
        <h1>Create New Session</h1>
        <p>Share your expertise with clients by creating a group session</p>

        {error && <div className="error-alert">{error}</div>}

        <form onSubmit={handleSubmit} className="session-form">
          {/* Title */}
          <div className="form-group">
            <label>Session Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Morning Meditation for Stress Relief"
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your session in detail..."
              rows="4"
            />
          </div>

          {/* Topic */}
          <div className="form-group">
            <label>Topic *</label>
            <select name="topic" value={formData.topic} onChange={handleChange} required>
              {topics.map((topic) => (
                <option key={topic} value={topic}>
                  {topic.replace('-', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Pricing */}
          <div className="form-group">
            <label>Price (₹) - Leave 0 for Free</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0"
              step="100"
            />
          </div>

          {/* Mode Selection */}
          <div className="form-group">
            <label>Session Mode *</label>
            <div className="mode-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="mode"
                  value="online"
                  checked={formData.mode === 'online'}
                  onChange={handleChange}
                />
                <span>Online</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="mode"
                  value="offline"
                  checked={formData.mode === 'offline'}
                  onChange={handleChange}
                />
                <span>Offline</span>
              </label>
            </div>
          </div>

          {/* Location (if offline) */}
          {formData.mode === 'offline' && (
            <div className="form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Enter session location/address"
                required
              />
            </div>
          )}

          {/* Zoom Link (if online) */}
          {formData.mode === 'online' && (
            <div className="form-group">
              <label>Zoom Meeting Link</label>
              <input
                type="url"
                name="zoomMeetingLink"
                value={formData.zoomMeetingLink}
                onChange={handleChange}
                placeholder="https://zoom.us/j/..."
              />
            </div>
          )}

          {/* Schedule */}
          <div className="form-row">
            <div className="form-group">
              <label>Start Time *</label>
              <input
                type="datetime-local"
                name="scheduledAt"
                value={formData.scheduledAt}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>End Time *</label>
              <input
                type="datetime-local"
                name="endsAt"
                value={formData.endsAt}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Max Participants */}
          <div className="form-group">
            <label>Max Participants</label>
            <input
              type="number"
              name="maxParticipants"
              value={formData.maxParticipants}
              onChange={handleChange}
              min="1"
              max="500"
            />
          </div>

          {/* Buttons */}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TherapistSessionCreationPage;
